import { Dictionary, get, groupBy } from 'lodash';

import { LIABILITY_TYPE, LIABILITY_TYPE_OPTIONS } from '@funnels/constants';
import { Liability, ValidateError } from '@funnels/types';

import { FIELD_PATHS } from './../../constants/src/fieldPaths';
import getOrdinal from './getOrdinal';
import { traverseAndFlatten } from './traverseAndFlatten';

const PLACEHOLDER = '%PLACEHOLDER%';

export const getValidateErrors = (errors: Object): ValidateError[] => {
  const validateErrorsObject: ValidateError = {};
  traverseAndFlatten(errors, validateErrorsObject);
  return Object.entries(validateErrorsObject).map(([key, val]) => ({
    name: key,
    message: val,
  }));
};

type LiabilityErrors = {
  type?: string;
  percentOwned?: {
    owners: string;
  };
  financialInstitution?: string;
  creditLimit?: string;
  outstandingBalance?: string;
  annualInterestRate?: string;
  repaymentAmount?: string;
  repaymentFrequency?: string;
  newLimit?: string;
  remainingTermDuration?: string;
  description?: string;
};

const liabilityTypes = [
  ...LIABILITY_TYPE_OPTIONS.filter((o) => o.value !== LIABILITY_TYPE.OTHER),
  {
    value: LIABILITY_TYPE.OTHER,
    label: 'Other type of liability',
  },
];

const getLiabilityTags = (
  liabilities: Liability[],
  index: number,
  liabilitiesGroupedByType: Dictionary<Liability[]>,
  liabilitiesGroupedByInstitution: Dictionary<Liability[]>,
  liabilitiesGroupedByTypeAndInstitution: Dictionary<Liability[]>
) => {
  const clientId = get({ liabilities }, FIELD_PATHS.LIABILITY_CLIENT_ID(index));
  const type = get({ liabilities }, FIELD_PATHS.LIABILITY_TYPE(index));
  const institution = get({ liabilities }, FIELD_PATHS.LIABILITY_FINANCIAL_INSTITUTION(index));
  const key = [type, institution].filter(Boolean).join('-');
  const group =
    !type && !institution // if no type and institution, just get ordinal from all liabilities
      ? liabilities
      : [liabilitiesGroupedByType, liabilitiesGroupedByInstitution, liabilitiesGroupedByTypeAndInstitution][
          +!!type + +!!institution * 2 - 1
        ][key];
  const ordinal = group.length > 1 ? `${getOrdinal(group.findIndex((l) => l.clientId === clientId) + 1, true)} ` : ' ';
  return `for your ${ordinal}${liabilityTypes.find((o) => o.value === type)?.label || 'liability'}${
    institution ? ` with ${institution}` : ''
  }`;
};

export const getLiabilityValidationErrors = (liabilities: Liability[], liabilityErrors: LiabilityErrors[]) => {
  const liabilitiesGroupedByType = groupBy(liabilities, 'type');
  const liabilitiesGroupedByInstitution = groupBy(liabilities, 'financialInstitution');
  const liabilitiesGroupedByTypeAndInstitution = groupBy(liabilities, (l) => `${l.type}-${l.financialInstitution}`);
  const errors: ValidateError[] = [];
  liabilityErrors.forEach((error, index) => {
    if (!error) return;
    const tags = getLiabilityTags(
      liabilities,
      index,
      liabilitiesGroupedByType,
      liabilitiesGroupedByInstitution,
      liabilitiesGroupedByTypeAndInstitution
    );
    if (error.type)
      errors.push({
        name: FIELD_PATHS.LIABILITY_TYPE(index),
        message: error.type.replace(PLACEHOLDER, tags),
      });
    if (error.percentOwned)
      errors.push({
        name: FIELD_PATHS.LIABILITY_OWNERS(index),
        message: error.percentOwned.owners.replace(PLACEHOLDER, tags),
      });
    if (error.financialInstitution)
      errors.push({
        name: FIELD_PATHS.LIABILITY_FINANCIAL_INSTITUTION(index),
        message: error.financialInstitution.replace(PLACEHOLDER, tags),
      });
    if (error.creditLimit)
      errors.push({
        name: FIELD_PATHS.LIABILITY_CREDIT_LIMIT(index),
        message: error.creditLimit.replace(PLACEHOLDER, tags),
      });
    if (error.outstandingBalance)
      errors.push({
        name: FIELD_PATHS.LIABILITY_OUTSTANDING_BALANCE(index),
        message: error.outstandingBalance.replace(PLACEHOLDER, tags),
      });

    if (error.annualInterestRate)
      errors.push({
        name: FIELD_PATHS.LIABILITY_INTEREST_RATE(index),
        message: error.annualInterestRate.replace(PLACEHOLDER, tags),
      });
    if (error.repaymentAmount)
      errors.push({
        name: FIELD_PATHS.LIABILITY_REPAYMENT_VALUE(index),
        message: error.repaymentAmount.replace(PLACEHOLDER, tags),
      });
    if (error.repaymentFrequency)
      errors.push({
        name: FIELD_PATHS.LIABILITY_REPAYMENT_FREQUENCY(index),
        message: error.repaymentFrequency.replace(PLACEHOLDER, tags),
      });
    if (error.newLimit)
      errors.push({
        name: FIELD_PATHS.LIABILITY_NEW_LIMIT(index),
        message: error.newLimit.replace(PLACEHOLDER, tags),
      });
    if (error.remainingTermDuration)
      errors.push({
        name: FIELD_PATHS.LIABILITY_REMAINING_TERM(index),
        message: error.remainingTermDuration.replace(PLACEHOLDER, tags),
      });
    if (error.description)
      errors.push({
        name: FIELD_PATHS.LIABILITY_DESCRIPTION(index),
        message: error.description.replace(PLACEHOLDER, tags),
      });
  });
  return errors;
};
